import React, { useState, useEffect } from "react";
import {
  Card,
  Grid,
  Icon,
  InputAdornment,
  Tooltip,
  TextField,
  MenuItem,
  RadioGroup,
  Radio,
  FormControlLabel,
  Select,
  FormControl,
  Autocomplete,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import MDBadge from "components/MDBadge";
import CenterPopup from "components/CommonComponent/RightPanelDrawer";
import RequiredLabel from "components/CommonComponent/RequiredLabel";
import { showAlert } from "components/CommonComponent/ShowAlert";
import {
  selectSx,
  radioLabelSx,
  autoCompleteProps,
  ISOToDDMMYYYY,
  wrapCell,
} from "components/CommonComponent/CommonFunction";
import { STATUS } from "components/common/constant";
import {
  addStation,
  getStationList,
  editStation,
  getStationDetailsById,
  deleteStation,
} from "actions/station";
import { getDepartmentList } from "actions/department";
import { useDispatch, useSelector } from "react-redux";
import { stationList } from "constants/station";
import { departmentList } from "constants/department";
import {
  DEFAULT_RECORDS_PER_PAGE,
  hasAccess,
} from "components/common/constant";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import { ArrowDropDown } from "@mui/icons-material";

const initialFields = {
  id: null,
  name: { value: "", error: "" },
  department: { value: "", error: "" },
  status: { value: "ACTIVE", error: "" },
};

function StationList() {
  const { notify } = useNotify();
  const dispatch = useDispatch();
  const stationListData = useSelector((state) => state.station.stationList);
  const departmentListData = useSelector(
    (state) => state.department.departmentList,
  );

  const authData = JSON.parse(localStorage.getItem("p")) || {};
  const currentRole = authData?.role;

  const canCreate = hasAccess("COMMON_MASTER", "CREATE")?.includes(currentRole);
  const canUpdate = hasAccess("COMMON_MASTER", "UPDATE")?.includes(currentRole);
  const canDelete = hasAccess("COMMON_MASTER", "DELETE")?.includes(currentRole);

  const canShowActions = canUpdate || canDelete;
  const [pageNum, setPageNum] = useState(1);
  const [panelMode, setPanelMode] = useState(null);
  const [stationFormData, setStationFormData] = useState(initialFields);
  const [isUsed, setIsUsed] = useState(false);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [filters, setFilters] = useState({
    name: "",
    department: "",
    status: "",
    filterParams: [],
  });

  useEffect(() => {
    getStationList(dispatch, pageNum, filters.filterParams);

    return () => {
      dispatch({ type: stationList, payload: {} });
    };
  }, [dispatch, pageNum, filters.filterParams]);

  useEffect(() => {
    getDepartmentList(dispatch);

    return () => {
      dispatch({ type: departmentList, payload: {} });
    };
  }, [dispatch]);

  useEffect(() => {
    if (stationListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: stationListData?.data?.station?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (stationListData?.code || stationListData?.message) {
      setListState({
        isLoading: false,
        error: stationListData?.message || "Failed to load station list!",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [stationListData]);

  const tableData =
    stationListData?.data?.station?.map((item, index) => ({
      ...item,
      id: item?.ID,
      srNo:
        (pageNum - 1) *
          (stationListData?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
      department: item?.department?.name || "-",
      name: item?.name || "-",
      status: item?.status || "-",
      createdAt: ISOToDDMMYYYY(item?.createdAt),
    })) || [];

  const buildFilterParams = () => {
    const params = [];

    if (filters.name?.trim()) {
      params.push({
        field: "Name",
        condition: "like",
        filterValues: [filters.name.trim()],
      });
    }

    if (filters.department) {
      params.push({
        field: "DeptID",
        condition: "eq",
        filterValues: [String(filters.department)],
      });
    }

    if (filters.status) {
      params.push({
        field: "Status",
        condition: "eq",
        filterValues: [filters.status],
      });
    }

    return params.length ? JSON.stringify(params) : [];
  };

  const handleSearch = () => {
    const newParams = buildFilterParams();

    setListState((prev) => ({
      ...prev,
      isLoading: true,
      showLoaderOnClick: true,
    }));

    setFilters((prev) => ({
      ...prev,
      filterParams: newParams,
    }));

    if (pageNum !== 1) setPageNum(1);
  };

  const handleChange = (field, value) => {
    setStationFormData((prev) => ({
      ...prev,
      [field]: { value, error: "" },
    }));
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "name":
        return !value || !value.trim() ? "Please enter station name" : "";

      case "department":
        return !value ? "Please select department" : "";

      default:
        return "";
    }
  };

  const handleOnBlur = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmed);

    setStationFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmed,
        error,
      },
    }));
  };

  const validateForm = () => {
    let newData = { ...stationFormData };

    Object.keys(newData).forEach((key) => {
      if (key === "id") return;

      newData[key] = {
        ...newData[key],
        error: getValidationError(key, newData[key].value),
      };
    });
    setStationFormData(newData);
    return Object.values(newData)
      .filter((item, i) => Object.keys(newData)[i] !== "id")
      .every((item) => item.error === "");
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: true,
      }));

      let payload = {};

      Object.keys(stationFormData).forEach((key) => {
        if (key === "id") return;

        const field = stationFormData[key];

        if (key === "department") {
          const departmentId = field.value?.ID || field.value?.id;

          if (panelMode !== "edit" || departmentId !== field.original?.ID) {
            payload.departmentId = departmentId;
          }
        } else {
          if (panelMode !== "edit" || field.value !== field.original) {
            payload[key] = field.value;
          }
        }
      });

      // no changes case (edit)
      if (panelMode === "edit" && !Object.keys(payload).length) {
        notify("No changes detected", "info");
        setListState((prev) => ({
          ...prev,
          showLoaderOnClick: false,
        }));
        return;
      }

      const res =
        panelMode === "edit"
          ? await editStation(stationFormData.id, payload)
          : await addStation(payload);

      if (res?.code === 200) {
        notify(
          res?.message ||
            (panelMode === "edit"
              ? "Station updated successfully"
              : "Station added successfully"),
          "success",
        );

        closeModal();

        // refresh list
        getStationList(dispatch, pageNum, filters.filterParams);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to save station";

        notify(message, "error");
      }
    } catch (error) {
      notify(error?.message || "Something went wrong", "error");
    } finally {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: false,
      }));
    }
  };

  const closeModal = () => {
    setPanelMode(null);
    setIsUsed(false);
    setStationFormData(initialFields);
  };

  const openAddModal = () => {
    setPanelMode("add");
    setIsUsed(false);
    setStationFormData(initialFields);
  };

  const openEditModal = async (row) => {
    setPanelMode("edit");

    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    const res = await getStationDetailsById(row.id);

    if (res?.code === 200) {
      const selectedDepartment =
        departmentListData?.data?.Department?.find(
          (d) => d.ID === res?.data?.department?.ID,
        ) || null;

      setIsUsed(res?.data?.isUsed ?? false);

      setStationFormData({
        id: res?.data?.ID,
        name: {
          value: res?.data?.name || "",
          original: res?.data?.name || "",
          error: "",
        },
        department: {
          value: selectedDepartment,
          original: selectedDepartment,
          error: "",
        },
        status: {
          value: res?.data?.status || "ACTIVE",
          original: res?.data?.status || "ACTIVE",
          error: "",
        },
      });
    } else {
      const message = Array.isArray(res?.message)
        ? res?.message
            .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
            .join(", ")
        : res?.message || "Failed to fetch station details";
      notify(message, "error");
    }

    setListState((prev) => ({
      ...prev,
      isLoading: false,
    }));
  };

  const handleDelete = (row) => {
    showAlert({
      title: "Are you sure you want to delete this station?",
      message: `Station "${row.name}" will be deleted permanently.`,
      type: "warning",
      showCancel: true,
      confirmText: "Yes, delete",
      cancelText: "Cancel",

      onConfirm: async () => {
        try {
          setListState((prev) => ({
            ...prev,
            isLoading: true,
          }));

          const res = await deleteStation(row.id);

          if (res?.code === 200) {
            notify(res?.message || "Station deleted successfully", "success");

            // refresh list
            getStationList(dispatch, pageNum, filters.filterParams);
          } else {
            notify(res?.message || "Failed to delete station", "error");
          }
        } catch (error) {
          notify(error?.message || "Something went wrong", "error");
        } finally {
          setListState((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      },

      onCancel: () => {},
    });
  };

  const handleClear = () => {
    setFilters({
      name: "",
      department: "",
      status: null,
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const renderStatusBadge = (status) => (
    <MDBadge
      badgeContent={status}
      color={status === "ACTIVE" ? "success" : "secondary"}
      variant="gradient"
      size="sm"
      circular
    />
  );

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const columns = [
    { Header: "Sr No.", accessor: "srNo", align: "left", width: "100px" },
    {
      Header: "Department",
      accessor: "department",
      align: "left",
      Cell: (cell) => wrapCell(cell.value, "180px"),
    },
    {
      Header: "Station Name",
      accessor: "name",
      align: "left",
      Cell: (cell) => wrapCell(cell.value, "200px"),
    },
    {
      Header: "Status",
      accessor: "status",
      align: "center",
      Cell: (cell) => renderStatusBadge(cell.row.original.status),
    },
    { Header: "Created Date", accessor: "createdAt", align: "center" },
    ...(canShowActions
      ? [
          {
            Header: "Action",
            accessor: "actions",
            align: "center",
            disableSortBy: true,
            Cell: (cell) => {
              const rowData = cell.row.original;

              return (
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  {canUpdate && (
                    <Tooltip title="Edit" arrow>
                      <Icon
                        sx={({ palette }) => ({
                          cursor: "pointer",
                          color: palette.secondary.main,
                        })}
                        onClick={() => openEditModal(rowData)}
                      >
                        <i className="fas fa-edit fa-sm" />
                      </Icon>
                    </Tooltip>
                  )}

                  {canUpdate && canDelete && (
                    <MDTypography variant="caption" mx={0.5}>
                      |
                    </MDTypography>
                  )}

                  {canDelete && (
                    <Tooltip title="Delete" arrow>
                      <Icon
                        color="error"
                        sx={{
                          cursor: "pointer",
                          opacity: 0.5,
                          "&:hover": { opacity: 1 },
                        }}
                        onClick={() => handleDelete(rowData)}
                      >
                        <i className="fas fa-trash" />
                      </Icon>
                    </Tooltip>
                  )}
                </MDBox>
              );
            },
          },
        ]
      : []),
  ];

  const departmentOptions = departmentListData?.data?.Department || [];

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox width="100%">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ width: "100%" }}>
              {/* Header */}
              <MDBox
                px={3}
                pt={3}
                pb={2}
                display="flex"
                justifyContent="space-between"
              >
                <MDTypography variant="h5" fontWeight="bold">
                  Stations
                </MDTypography>

                {canCreate && (
                  <MDButton
                    variant="contained"
                    color="deepBlue"
                    sx={{
                      py: 1.2,
                      px: 3,
                      fontWeight: "bold",
                      fontSize: "0.9rem",
                      textTransform: "none",
                    }}
                    onClick={openAddModal}
                  >
                    Add Station
                  </MDButton>
                )}
              </MDBox>

              {/* Filters */}
              <MDBox px={3} pb={2} onKeyDown={handleKeyDown}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <Autocomplete
                      {...autoCompleteProps}
                      disablePortal
                      size="small"
                      options={departmentOptions}
                      getOptionLabel={(option) => option.name || ""}
                      value={
                        departmentOptions.find(
                          (dept) => dept.ID === filters.department,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        setFilters({
                          ...filters,
                          department: newValue?.ID || "",
                        })
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.ID === value?.ID
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select department"
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by name"
                      value={filters.name}
                      onChange={(e) =>
                        setFilters({ ...filters, name: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <FormControl fullWidth size="small" sx={selectSx("100%")}>
                      <Select
                        value={filters.status || ""}
                        displayEmpty
                        IconComponent={ArrowDropDown}
                        onChange={(e) =>
                          setFilters({ ...filters, status: e.target.value })
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSearch();
                          }
                        }}
                        renderValue={(selected) =>
                          !selected ? (
                            <span style={{ color: "rgba(0,0,0,0.38)" }}>
                              Select Status
                            </span>
                          ) : (
                            renderStatusBadge(selected)
                          )
                        }
                      >
                        {STATUS.map((status) => (
                          <MenuItem key={status.label} value={status.label}>
                            {renderStatusBadge(status.label)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                    <MDBox
                      display="flex"
                      gap={1}
                      flexWrap="nowrap"
                      width="100%"
                    >
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        sx={{
                          flex: 1,
                          minWidth: "90px",
                          maxWidth: "160px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={handleSearch}
                        disabled={listState.showLoaderOnClick}
                      >
                        {listState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>

                      {filters.filterParams.length > 0 && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          sx={{
                            flex: 1,
                            minWidth: "90px",
                            maxWidth: "160px",
                            whiteSpace: "nowrap",
                          }}
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Total Records */}
              <MDBox px={3} py={1}>
                <MDTypography variant="body1" color="deepBlue" mb={1}>
                  Total Records: {stationListData?.data?.totalRecords || 0}
                </MDTypography>
              </MDBox>

              {/* Table */}
              <MDBox sx={{ width: "100%", overflowX: "auto" }}>
                {listState.isLoading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </MDBox>
                ) : listState.error ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="error">
                      {listState.error}
                    </MDTypography>
                  </MDBox>
                ) : listState.noRecords ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="text">
                      No stations found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    columns={columns}
                    data={tableData || []}
                    isSorted={true}
                    pageNum={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      (stationListData?.data?.totalRecords || 0) /
                        (stationListData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                    )}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Drawer */}
        <CenterPopup isOpen={panelMode !== null} onClose={closeModal}>
          <MDBox sx={{ p: 2 }}>
            <MDTypography variant="h6" mb={0}>
              {panelMode === "edit" ? "Edit Station" : "Add Station"}
            </MDTypography>

            {panelMode === "edit" && isUsed && (
              <Grid item xs={12} sx={{ mb: 1 }}>
                <MDTypography
                  variant="caption"
                  sx={{
                    color: "#fc6e0f",
                  }}
                >
                  <b>Note:</b> This station is used in timesheets, so Department
                  and Name cannot be edited.
                </MDTypography>
              </Grid>
            )}

            {listState.isLoading ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="150px"
              >
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </MDBox>
            ) : (
              <Grid container spacing={2}>
                {/* Department */}
                <Grid item xs={12}>
                  <RequiredLabel required>Department</RequiredLabel>

                  <Autocomplete
                    {...autoCompleteProps}
                    size="small"
                    disablePortal
                    disabled={panelMode === "edit" && isUsed}
                    options={departmentOptions}
                    getOptionLabel={(option) => option.name || ""}
                    value={stationFormData.department.value || null}
                    onChange={(event, newValue) =>
                      handleChange("department", newValue)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?.ID === value?.ID
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Department"
                        error={!!stationFormData.department.error}
                        helperText={stationFormData.department.error}
                      />
                    )}
                  />
                </Grid>

                {/* Station Name */}
                <Grid item xs={12}>
                  <RequiredLabel required>Name</RequiredLabel>
                  <MDInput
                    variant="outlined"
                    type="text"
                    name="name"
                    placeholder="Enter station name"
                    value={stationFormData.name.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    error={!!stationFormData.name.error}
                    fullWidth
                    disabled={panelMode === "edit" && isUsed}
                    inputProps={{ maxLength: 50 }}
                  />
                  {stationFormData.name.error && (
                    <MDTypography variant="caption" color="error">
                      {stationFormData.name.error}
                    </MDTypography>
                  )}
                </Grid>

                {/* Status */}
                <Grid item xs={12}>
                  <RequiredLabel>Status</RequiredLabel>
                  <RadioGroup
                    row
                    name="status"
                    value={stationFormData.status.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                  >
                    <FormControlLabel
                      value="ACTIVE"
                      control={<Radio size="small" />}
                      label="Active"
                      sx={radioLabelSx}
                    />
                    <FormControlLabel
                      value="INACTIVE"
                      control={<Radio size="small" />}
                      label="Inactive"
                      sx={radioLabelSx}
                    />
                  </RadioGroup>
                </Grid>

                {/* Buttons */}
                <Grid item xs={12}>
                  <MDBox display="flex" justifyContent="flex-end">
                    <MDButton
                      variant="gradient"
                      color="light"
                      onClick={closeModal}
                      sx={{ mr: 2 }}
                    >
                      Cancel
                    </MDButton>

                    <MDButton
                      type="submit"
                      variant="contained"
                      color="deepBlue"
                      onClick={handleSubmit}
                      disabled={listState.showLoaderOnClick}
                    >
                      {listState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin" />
                      ) : (
                        "Save"
                      )}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            )}
          </MDBox>
        </CenterPopup>
      </MDBox>
    </DashboardLayout>
  );
}

export default StationList;
